// "Add by hand": a menu with the form for one company (saved as a pre-lead to review) and the filter search page.
import { ChevronDown, Loader2, Plus, SlidersHorizontal } from "lucide-react";
import {
  useDataProvider,
  useGetIdentity,
  useNotify,
  useRefresh,
  useTranslate,
} from "ra-core";
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { domainOf } from "../../../../../supabase/functions/_shared/leadFilters.ts";

const FIELDS = [
  ["company_name", "text"],
  ["website", "url"],
  ["contact_first_name", "text"],
  ["contact_last_name", "text"],
  ["contact_title", "text"],
  ["contact_email", "email"],
] as const;
type Field = (typeof FIELDS)[number][0];
const blank = () =>
  Object.fromEntries(FIELDS.map(([f]) => [f, ""])) as Record<Field, string>;

export const AddPreLeadDialog = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const { identity } = useGetIdentity();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState(blank);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    const v = Object.fromEntries(
      Object.entries(values).map(([k, x]) => [k, x.trim() || null]),
    ) as Record<Field, string | null>;
    if (!v.company_name) return;
    setSaving(true);
    try {
      await dataProvider.create("pre_leads", {
        data: {
          ...v,
          domain: domainOf(v.website) ?? null,
          status: "found",
          source: "manual",
          ref: `manual:${Date.now()}`,
          sales_id: identity?.id,
          fit: null,
        },
      });
      notify("resources.pre_leads.notify.created", { type: "success" });
      setValues(blank());
      setOpen(false);
      refresh();
    } catch (error) {
      notify((error as Error).message, { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="max-md:min-h-11">
            <Plus />
            {translate("resources.pre_leads.add_by_hand")}
            <ChevronDown />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => setOpen(true)}>
            <Plus />
            {translate("resources.pre_leads.add_by_hand")}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => navigate("/leads/search")}>
            <SlidersHorizontal />
            {translate("resources.pre_leads.search_by_filters")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <form onSubmit={save} className="flex flex-col gap-3">
            <DialogHeader>
              <DialogTitle>
                {translate("resources.pre_leads.add_title")}
              </DialogTitle>
            </DialogHeader>
            {FIELDS.map(([field, type]) => (
              <div key={field} className="flex flex-col gap-1">
                <Label htmlFor={`pre-lead-${field}`}>
                  {translate(`resources.pre_leads.fields.${field}`)}
                  {field === "company_name" && " *"}
                </Label>
                <Input
                  id={`pre-lead-${field}`}
                  type={type === "url" ? "text" : type}
                  inputMode={type === "url" ? "url" : undefined}
                  required={field === "company_name"}
                  value={values[field]}
                  onChange={(e) =>
                    setValues({ ...values, [field]: e.target.value })
                  }
                />
              </div>
            ))}
            <DialogFooter>
              <Button
                type="submit"
                disabled={saving || !values.company_name.trim()}
              >
                {saving ? <Loader2 className="animate-spin" /> : <Plus />}
                {translate("ra.action.save")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
